import { Box, Typography, useTheme, Paper } from "@mui/material";
import { TableHeaderContainer } from "../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../components/container/TableTitleContainer";
import mime from "mime";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FiBookOpen, FiClipboard, FiClock, FiFile, FiHome, FiMapPin } from "react-icons/fi";
import FileUpload from "../components/input/FileUpload";
import ImageUpload from "../components/input/ImageUpload";
import ResizableTextBox from "../components/input/ResizableTextBox";
import TextBox from "../components/input/TextBox";
import { useContextProvider } from "../components/page-context/ContextProvider";
import PrimarySelect from "../components/select/PrimarySelect";
import { Building } from "../db_access/building";
import { getOrgTokens, ORG_PALETTE } from "../theme/style/organizationPalette";

const BuildingInfo = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { selectedBuildings, isSaveClicked, setIsSaveClicked, isSaveVisible, setIsSaveVisible, setIsLoading, setRefresh } = useContextProvider();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    id: "",
    intro: "",
    place_of_manufacture: "",
    builtup_area: "",
    service_life: "",
    extended_service_life: "",
    specifications: "",
  });
  const [selectedBuilding, setSelectedBuilding] = useState();
  const [photo, setPhoto] = useState();
  const [photoPreview, setPhotoPreview] = useState();
  const [specDocument, setSpecDocument] = useState();
  const [specDocumentPreview, setSpecDocumentPreview] = useState();
  const [safetyDocument, setSafetyDocument] = useState();
  const [safetyDocumentPreview, setSafetyDocumentPreview] = useState();
  const [modified, setModified] = useState(false);
  const ref = useRef(null);

  const [unit, setUnit] = useState("sqft"); // Default UI unit

  useEffect(() => {
    if (selectedBuildings.length < 1) {
      setSelectedBuilding(undefined);
    } else {
      setSelectedBuilding(selectedBuildings[0]);
    }
  }, [selectedBuildings]);

  // Load building data when selected
  useEffect(() => {
    if (selectedBuilding) {
      // Assuming DB stores in m2, convert to sqft for display if sqft is selected
      const dbArea = selectedBuilding.builtup_area || "";
      const displayArea = unit === "sqft" && dbArea ? (dbArea * 10.7639).toFixed(2) : dbArea;

      setFormData({
        id: selectedBuilding.id,
        intro: selectedBuilding.product_intro || "",
        specifications: selectedBuilding.specifications || "",
        place_of_manufacture: selectedBuilding.place_of_manufacture || "",
        builtup_area: displayArea,
        service_life: selectedBuilding.service_life || "",
        extended_service_life: selectedBuilding.extended_service_life || "",
      });
      setModified(false);
    }
  }, [selectedBuilding, unit]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData({ ...formData, [field]: value });
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleUnitChange = (newUnit) => {
    if (newUnit === unit) return;

    // Convert current display value to new unit display
    const currentVal = parseFloat(formData.builtup_area);
    if (!isNaN(currentVal)) {
      const converted = newUnit === "sqft" ? (currentVal * 10.7639).toFixed(2) : (currentVal / 10.7639).toFixed(2);
      setFormData((prev) => ({ ...prev, builtup_area: converted }));
    }
    setUnit(newUnit);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhoto(file || undefined);
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleSpecDocumentChange = (e) => {
    const file = e.target.files[0];
    setSpecDocument(file || undefined);
    setIsSaveVisible(true);
    setModified(true);
  };

  const handleSafetyDocumentChange = (e) => {
    const file = e.target.files[0];
    setSafetyDocument(file || undefined);
    setIsSaveVisible(true);
    setModified(true);
  };

  // Preview photo
  useEffect(() => {
    if (photo && photo instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(photo);
    } else if (photo instanceof Blob) {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(photo);
    } else {
      setPhotoPreview(undefined);
    }
  }, [photo]);

  // Preview spec document
  useEffect(() => {
    if (specDocument && specDocument instanceof File) {
      setSpecDocumentPreview("spec_document." + mime.getExtension(specDocument.type));
    } else {
      setSpecDocumentPreview(undefined);
    }
  }, [specDocument]);

  // Preview safety document
  useEffect(() => {
    if (safetyDocument && safetyDocument instanceof File) {
      setSafetyDocumentPreview("safety_document." + mime.getExtension(safetyDocument.type));
    } else {
      setSafetyDocumentPreview(undefined);
    }
  }, [safetyDocument]);

  // Load photo from backend
  useEffect(() => {
    if (selectedBuildings[0]?.id) {
      Building.get_photo(selectedBuildings[0].id)
        .then((res) => (res.ok ? res.blob() : undefined))
        .then((blob) => setPhoto(blob))
        .catch(() => setPhoto(undefined));
    }
  }, [selectedBuildings]);

  // Load spec document from backend
  useEffect(() => {
    if (selectedBuildings[0]?.id) {
      Building.get_spec_document(selectedBuildings[0].id)
        .then((res) => (res.ok ? res.blob() : undefined))
        .then((blob) => setSpecDocument(blob))
        .catch(() => setSpecDocument(undefined));
    }
  }, [selectedBuildings]);

  // Load safety document from backend
  useEffect(() => {
    if (selectedBuildings[0]?.id) {
      Building.get_safety_document(selectedBuildings[0].id)
        .then((res) => (res.ok ? res.blob() : undefined))
        .then((blob) => setSafetyDocument(blob))
        .catch(() => setSafetyDocument(undefined));
    }
  }, [selectedBuildings]);

  // Save when Save button clicked
  useEffect(() => {
    if (isSaveClicked && modified) {
      setIsLoading(true);

      const fd = new FormData();
      // Add all fields except potentially modified ones
      Object.keys(formData).forEach((k) => {
        if (k !== "builtup_area") {
          fd.append(k, formData[k]);
        }
      });

      // Handle area conversion to m2 for DB storage
      const areaVal = parseFloat(formData.builtup_area);
      const areaToSave = unit === "sqft" && !isNaN(areaVal) ? areaVal / 10.7639 : areaVal;
      fd.append("builtup_area", isNaN(areaToSave) ? "" : areaToSave);

      if (photo instanceof File) {
        fd.append("photo", photo);
      }
      if (specDocument instanceof File) {
        fd.append("specDocument", specDocument);
      }
      if (safetyDocument instanceof File) {
        fd.append("safetyDocument", safetyDocument);
      }

      Building.save_building_info(fd)
        .then((res) => {
          if (!res.ok) throw new Error("Save failed");
          return res.json();
        })
        .catch((err) => {
          console.error("Error saving building info:", err);
          alert("Failed to save building info. Check console for details.");
        })
        .finally(() => {
          setIsSaveClicked(false);
          setIsSaveVisible(false);
          setIsLoading(false);
          setModified(false);
          setRefresh((prev) => !prev);

          // Invalidate building results as parameters like service life or area might have changed
          if (formData.id) {
            queryClient.invalidateQueries({ queryKey: ["building", formData.id, "results"] });
          }
        });
    }
  }, [isSaveClicked, modified, formData, photo, specDocument, safetyDocument, unit, queryClient]);

  return (
    <Paper variant="Table Container">
      <TableHeaderContainer>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Building Info")}</Typography>
        </TableTitleContainer>
      </TableHeaderContainer>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }} ref={ref}>
          {/* LEFT COLUMN: Input Fields */}
          <Box sx={{ flex: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            <ResizableTextBox Icon={FiBookOpen} label="Building Introduction" placeholder="Building Introduction" value={formData.intro} onChange={handleChange("intro")} />
            <ResizableTextBox Icon={FiClipboard} label="Specifications" placeholder="Specifications" value={formData.specifications} onChange={handleChange("specifications")} />
            
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
              <Box sx={{ flex: 1 }}>
                <TextBox
                  Icon={FiHome}
                  label={`Built-up Area (${unit === "sqft" ? "sqft" : "m²"})`}
                  placeholder={`Built-up Area in ${unit === "sqft" ? "square feet" : "square meters"}`}
                  value={formData.builtup_area}
                  onChange={handleChange("builtup_area")}
                />
              </Box>
              <Box sx={{ minWidth: 90, pt: "26px" }}>
                <PrimarySelect
                  options={[
                    { label: "sqft", value: "sqft" },
                    { label: "m²", value: "sqmtr" },
                  ]}
                  selected={unit}
                  setSelected={handleUnitChange}
                />
              </Box>
            </Box>

            <TextBox Icon={FiClock} label="Service Life (years)" placeholder="Years" value={formData.service_life} onChange={handleChange("service_life")} />
            <TextBox Icon={FiClock} label="Extended Service Life (years)" placeholder="Years" value={formData.extended_service_life} onChange={handleChange("extended_service_life")} />
            
            <Box sx={{ display: "flex", gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <TextBox
                  Icon={FiMapPin}
                  label="Place of Manufacture"
                  placeholder="Place of Manufacture"
                  value={formData.place_of_manufacture}
                  onChange={handleChange("place_of_manufacture")}
                />
              </Box>
              <Box sx={{ flex: 1 }} />
            </Box>
          </Box>

          {/* RIGHT COLUMN: Media & Documents */}
          <Box sx={{ flex: 1, maxWidth: { md: 400 }, width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
            <ImageUpload
              Icon={FiFile}
              label="Building Photo"
              placeholder="Click to upload photo"
              onChange={handlePhotoChange}
              preview={photoPreview}
              alt="Building"
              accept="image/*"
              deletePreview={() => {
                setPhoto(undefined);
                setIsSaveVisible(true);
                setModified(true);
              }}
            />
            <FileUpload
              Icon={FiFile}
              label="Specification Document"
              preview={specDocumentPreview}
              placeholder="Click to upload document"
              onChange={handleSpecDocumentChange}
              accept=".pdf,.doc,.docx"
              deletePreview={() => {
                setSpecDocument(undefined);
                setIsSaveVisible(true);
                setModified(true);
              }}
              file={specDocument}
            />
            <FileUpload
              Icon={FiFile}
              label="Building Safety Document"
              preview={safetyDocumentPreview}
              placeholder="Click to upload document"
              onChange={handleSafetyDocumentChange}
              accept=".pdf,.doc,.docx"
              deletePreview={() => {
                setSafetyDocument(undefined);
                setIsSaveVisible(true);
                setModified(true);
              }}
              file={safetyDocument}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default BuildingInfo;

